import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { toastOptions } from '../../tools/helpers/ToastHelper.ts';
import { useUserSelect } from '../../apis/user/useUserSelect.ts';
import type { ColDef, GridApi } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { parseStringToDate } from '../../tools/converters/DateConverter.ts';
import type { UserUserDeactivateActivateInput, UserUserIO } from '../../generated/fresh-guard-api';
import type { AgGridReact } from 'ag-grid-react';
import RoleIconCellView from './shared/role-icon-cell/RoleIconCell.view.tsx';
import ActionsCellView from './shared/actions-cell/ActionsCell.view.tsx';
import { useUserManagement } from '../../provider/user-management/UserManagement.hook.ts';
import { useUsersDeactivateActivateOptimistic } from '../../apis/user/useUsersDeactivateActivateOptimistic.ts';
import { lsLoad } from '../../tools/Storage.ts';
import StorageEnum from '../../enums/Storage.enum.ts';

export const useUserManagementController = () => {
  const username = lsLoad(StorageEnum.UserProfile).username;
  const { t } = useTranslation();
  const { users, setUsers, updateUserState, updateDeactivatedOrActivatedUsers } =
    useUserManagement();
  const gridRef = useRef<AgGridReact<UserUserIO>>(null);
  const [currentUser, setCurrentUser] = useState<UserUserIO | null>(null);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isUserMarketsDialogOpen, setIsUserMarketsDialogOpen] = useState(false);
  const [isUserResetPasswordDialogOpen, setIsUserResetPasswordDialogOpen] = useState(false);

  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'role',
      cellRenderer: RoleIconCellView,
      width: 50,
      sortable: false,
      filter: false,
    },
    {
      field: 'username',
      headerName: t('table.userManagement.username'),
      sortable: true,
      filter: true,
    },
    {
      field: 'firstname',
      headerName: t('table.userManagement.firstname'),
      sortable: true,
      filter: true,
    },
    {
      field: 'lastname',
      headerName: t('table.userManagement.lastname'),
      sortable: true,
      filter: true,
    },
    {
      field: 'markets',
      headerName: t('table.userManagement.markets'),
      sortable: true,
      filter: true,
    },
    {
      field: 'email',
      headerName: t('table.userManagement.email'),
      sortable: true,
      filter: true,
    },
    {
      field: 'languageCode',
      headerName: t('table.userManagement.languageCode'),
      sortable: false,
    },
    {
      field: 'deactivated',
      headerName: t('table.userManagement.deactivated'),
      sortable: false,
      filter: true,
      valueFormatter: (params) => {
        if (!params.value?.date) return '';

        return parseStringToDate(params.value.date, t) ?? '';
      },
    },
    {
      field: 'deactivatedBy',
      headerName: t('table.userManagement.deactivatedBy'),
      sortable: false,
    },
    {
      headerName: t('table.general.actions'),
      field: 'actions',
      cellRenderer: ActionsCellView,
      cellRendererParams: {
        setCurrentUser: setCurrentUser,
        setIsUserDialogOpen: setIsUserDialogOpen,
        setIsUserMarketsDialogOpen: setIsUserMarketsDialogOpen,
        setIsUserResetPasswordDialogOpen: setIsUserResetPasswordDialogOpen,
      },
      pinned: 'right',
      width: 140,
      sortable: false,
      filter: false,
    },
  ];

  const { data, isLoading } = useUserSelect();
  const { mutate, isPending } = useUsersDeactivateActivateOptimistic();

  const deactivateSelected = () => {
    const api = gridRef.current?.api as GridApi<UserUserIO>;
    const selected = api.getSelectedRows();
    const usersForDeactivation = selected
      .map((user) => user.username)
      .filter((username): username is string => username !== undefined);

    callDeactivateActivateApi({
      usersForDeactivation: usersForDeactivation,
    });
  };

  const activateSelected = () => {
    const api = gridRef.current?.api as GridApi<UserUserIO>;
    const selected = api.getSelectedRows();
    const usersForActivation = selected
      .map((user) => user.username)
      .filter((username): username is string => username !== undefined);

    callDeactivateActivateApi({
      usersForActivation: usersForActivation,
    });
  };

  const callDeactivateActivateApi = (
    userUserDeactivateActivateInput: UserUserDeactivateActivateInput
  ) => {
    mutate(
      { userUserDeactivateActivateInput, currentUsername: username },
      {
        onSuccess: () => {
          updateDeactivatedOrActivatedUsers(userUserDeactivateActivateInput);
          toast.success(t('success.userSaved'), toastOptions);
        },
      }
    );
  };

  const addUser = () => {
    setCurrentUser(null);
    setIsUserDialogOpen(true);
  };

  const onDialogCloseClick = () => {
    setIsUserMarketsDialogOpen(false);
    setIsUserResetPasswordDialogOpen(false);
  };

  const onUserDialogCloseClick = (user: UserUserIO | undefined) => {
    if (user && !currentUser) {
      setCurrentUser(user);
      setIsUserDialogOpen(false);
      setIsUserMarketsDialogOpen(true);
      updateUserState([user]);
    } else {
      setIsUserDialogOpen(false);
      setIsUserMarketsDialogOpen(false);
      setIsUserResetPasswordDialogOpen(false);

      if (user) {
        updateUserState([user]);
      }
    }
  };

  useEffect(() => {
    if (data) {
      setUsers(data);
    }
  }, [data, setUsers]);

  return {
    deactivateSelected,
    activateSelected,
    addUser,
    onDialogCloseClick,
    onUserDialogCloseClick,
    gridRef,
    users,
    columnDefs,
    isUserDialogOpen,
    isUserMarketsDialogOpen,
    isUserResetPasswordDialogOpen,
    currentUser,
    loading: isLoading || isPending,
  };
};
